import { DAYS_PER_YEAR } from 'features/dataLayers/generation/calculations/calculateDerivedValues'
import { convertNumber, GeckoMeasurementUnit } from 'types'

import { TARGET_FEATURE_RECT_PADDING_FEET } from './constants'
import { getCellBoundsFromCells } from './geometry'
import type {
  ShortTermCorrosionCellBounds,
  ShortTermGridValues,
  ShortTermRateFeature,
  ShortTermRateFeatureBounds,
  ShortTermRateFeatureCell,
  ShortTermRateFeatureGridMetadata,
  ValuePair,
} from './types'

const SUPPORTED_THICKNESS_UNITS_BY_ID = new Map<string, GeckoMeasurementUnit>([
  [GeckoMeasurementUnit.Millimeters.id, GeckoMeasurementUnit.Millimeters],
  [GeckoMeasurementUnit.Inches.id, GeckoMeasurementUnit.Inches],
  [GeckoMeasurementUnit.Feet.id, GeckoMeasurementUnit.Feet],
  [GeckoMeasurementUnit.Meters.id, GeckoMeasurementUnit.Meters],
  [GeckoMeasurementUnit.Mil.id, GeckoMeasurementUnit.Mil],
])

export function validate2DArrayShape(
  values: ShortTermGridValues,
  grid: ShortTermRateFeatureGridMetadata
) {
  if (values instanceof Float32Array) {
    if (values.length !== grid.xBinCount * grid.yBinCount) {
      throw new Error('CDF values do not match the expected bin count.')
    }
    return
  }

  if (values.length !== grid.yBinCount) {
    throw new Error('CDF values do not match the expected y-bin count.')
  }
  if ((values[0]?.length ?? 0) !== grid.xBinCount) {
    throw new Error('CDF values do not match the expected x-bin count.')
  }
}

export function getGridValue(
  values: ShortTermGridValues,
  grid: ShortTermRateFeatureGridMetadata,
  x: number,
  y: number
): number {
  if (values instanceof Float32Array) {
    return values[y * grid.xBinCount + x] ?? Number.NaN
  }

  return values[y]?.[x] ?? Number.NaN
}

export function createFilled2D<T>(
  rowCount: number,
  columnCount: number,
  fillValue: T
): T[][] {
  return Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, () => fillValue)
  )
}

export function getElapsedYears(
  comparisonDate: string,
  targetDate: string
): number {
  const comparisonTime = Date.parse(comparisonDate)
  const targetTime = Date.parse(targetDate)

  if (!Number.isFinite(comparisonTime) || !Number.isFinite(targetTime)) {
    throw new Error(
      'Short-term corrosion rate requires valid inspection dates.'
    )
  }

  return (targetTime - comparisonTime) / (1000 * 60 * 60 * 24 * DAYS_PER_YEAR)
}

export function getMillimetersMultiplier(measurementUnitId: string): number {
  const measurementUnit = SUPPORTED_THICKNESS_UNITS_BY_ID.get(measurementUnitId)

  if (!measurementUnit) {
    throw new Error(
      `Unsupported thickness unit for short-term corrosion rate: ${measurementUnitId}`
    )
  }

  return convertNumber(1, measurementUnit, GeckoMeasurementUnit.Millimeters)
}

export function getFeatureRectangleCells({
  targetFeature,
  targetCellBounds,
  targetGrid,
  paddingFeet = TARGET_FEATURE_RECT_PADDING_FEET,
}: {
  targetFeature?: ShortTermRateFeature
  targetCellBounds?: ShortTermCorrosionCellBounds
  targetGrid: ShortTermRateFeatureGridMetadata
  paddingFeet?: number
}): ShortTermRateFeatureCell[] {
  const paddingXCells = Math.ceil(
    Math.max(0, paddingFeet) / targetGrid.binWidthFeet
  )
  const paddingYCells = Math.ceil(
    Math.max(0, paddingFeet) / targetGrid.binHeightFeet
  )
  const cellBounds =
    targetCellBounds ??
    (targetFeature ? getCellBoundsFromCells(targetFeature.cells) : undefined)
  if (!cellBounds) return []

  const clampedMinX = clamp(
    cellBounds.minX - paddingXCells,
    0,
    targetGrid.xBinCount - 1
  )
  const clampedMaxX = clamp(
    cellBounds.maxX + paddingXCells,
    0,
    targetGrid.xBinCount - 1
  )
  const clampedMinY = clamp(
    cellBounds.minY - paddingYCells,
    0,
    targetGrid.yBinCount - 1
  )
  const clampedMaxY = clamp(
    cellBounds.maxY + paddingYCells,
    0,
    targetGrid.yBinCount - 1
  )
  const rectangleCells: ShortTermRateFeatureCell[] = []

  for (let y = clampedMinY; y <= clampedMaxY; y += 1) {
    for (let x = clampedMinX; x <= clampedMaxX; x += 1) {
      rectangleCells.push({ x, y })
    }
  }

  return rectangleCells
}

export function collectShiftedValuePairs({
  cells,
  targetValues,
  comparisonValues,
  targetGrid,
  comparisonGrid,
  shiftXFeet,
  shiftYFeet,
}: {
  cells: readonly ShortTermRateFeatureCell[]
  targetValues: ShortTermGridValues
  comparisonValues: ShortTermGridValues
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  shiftXFeet: number
  shiftYFeet: number
}): { pairs: ValuePair[]; coverageFraction: number } {
  const pairs: ValuePair[] = []
  let finiteTargetCellCount = 0

  for (const cell of cells) {
    const targetValue = getGridValue(targetValues, targetGrid, cell.x, cell.y)
    if (!Number.isFinite(targetValue)) continue

    finiteTargetCellCount += 1
    const targetCenter = getTargetCellCenterFeet(cell, targetGrid)
    const comparisonValue = sampleGridAtPhysicalPoint({
      values: comparisonValues,
      grid: comparisonGrid,
      xFeet: targetCenter.xFeet + shiftXFeet,
      yFeet: targetCenter.yFeet + shiftYFeet,
    })
    if (
      typeof comparisonValue !== 'number' ||
      !Number.isFinite(comparisonValue)
    ) {
      continue
    }

    pairs.push({ targetValue, comparisonValue })
  }

  return {
    pairs,
    coverageFraction:
      finiteTargetCellCount > 0 ? pairs.length / finiteTargetCellCount : 0,
  }
}

export function calculatePopulationStandardDeviation(
  values: readonly number[],
  mean: number
): number {
  if (values.length === 0) return 0

  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length

  return Math.sqrt(variance)
}

export function getPhysicalBoundsForCellBounds(
  cellBounds: ShortTermCorrosionCellBounds,
  grid: ShortTermRateFeatureGridMetadata
): ShortTermRateFeatureBounds {
  return {
    minXFeet: grid.xStartFeet + cellBounds.minX * grid.binWidthFeet,
    maxXFeet: grid.xStartFeet + (cellBounds.maxX + 1) * grid.binWidthFeet,
    minYFeet: grid.yStartFeet + cellBounds.minY * grid.binHeightFeet,
    maxYFeet: grid.yStartFeet + (cellBounds.maxY + 1) * grid.binHeightFeet,
  }
}

export function clampCellBoundsToGrid(
  cellBounds: ShortTermCorrosionCellBounds,
  grid: ShortTermRateFeatureGridMetadata
): ShortTermCorrosionCellBounds {
  return {
    minX: clamp(
      Math.min(cellBounds.minX, cellBounds.maxX),
      0,
      grid.xBinCount - 1
    ),
    maxX: clamp(
      Math.max(cellBounds.minX, cellBounds.maxX),
      0,
      grid.xBinCount - 1
    ),
    minY: clamp(
      Math.min(cellBounds.minY, cellBounds.maxY),
      0,
      grid.yBinCount - 1
    ),
    maxY: clamp(
      Math.max(cellBounds.minY, cellBounds.maxY),
      0,
      grid.yBinCount - 1
    ),
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function sampleGridAtPhysicalPoint({
  values,
  grid,
  xFeet,
  yFeet,
}: {
  values: ShortTermGridValues
  grid: ShortTermRateFeatureGridMetadata
  xFeet: number
  yFeet: number
}): number | undefined {
  const x = Math.floor((xFeet - grid.xStartFeet) / grid.binWidthFeet)
  const y = Math.floor((yFeet - grid.yStartFeet) / grid.binHeightFeet)

  if (x < 0 || x >= grid.xBinCount || y < 0 || y >= grid.yBinCount) {
    return undefined
  }

  return getGridValue(values, grid, x, y)
}

export function getTargetCellCenterFeet(
  cell: ShortTermRateFeatureCell,
  grid: ShortTermRateFeatureGridMetadata
) {
  return {
    xFeet: grid.xStartFeet + (cell.x + 0.5) * grid.binWidthFeet,
    yFeet: grid.yStartFeet + (cell.y + 0.5) * grid.binHeightFeet,
  }
}
